import type { PromotionView } from '@/api/generated';
import { moneyIn } from '@/components/moneyIn';
import { bpToPercentText, firstCurrency, formatCivilDate, formatCount } from '@/i18n/format';
import i18n from '@/i18n/i18n';

function tr(key: string, options?: Record<string, string | number>): string {
  return options ? i18n.t(key, options) : i18n.t(key);
}

/** Canvas discount vocabulary from the wire kind + fields. */
export function discountLabel(
  promo: PromotionView,
  locale: string,
  fallbackCurrency: string,
): string {
  if (promo.kind === 'OFFER') return tr('promotions.kind.offer');
  if (promo.kind === 'TRIAL_DAYS') {
    if (promo.trialDays == null) return tr('promotions.unavailable');
    return tr('promotions.kind.trial', { n: promo.trialDays });
  }
  if (promo.kind === 'AMOUNT_OFF_VARIABLE') {
    if (promo.amountMinor == null) return tr('promotions.unavailable');
    const currency = firstCurrency(promo.currency, fallbackCurrency);
    return tr('promotions.kind.amount', {
      amount: moneyIn(promo.amountMinor, currency, locale, tr('promotions.unavailable')),
    });
  }
  if (promo.percentBp == null) return tr('promotions.unavailable');
  if (promo.percentBp === 10000) return tr('promotions.kind.free');
  return tr('promotions.kind.percent', { pct: bpToPercentText(promo.percentBp, locale) });
}

/** `statements` null/absent = lifetime. */
export function durationLabel(promo: PromotionView): string {
  if (promo.statements === undefined || promo.statements === null) {
    return tr('promotions.duration.lifetime');
  }
  return tr('promotions.duration.statements', { count: promo.statements, n: promo.statements });
}

/** `12 / 50` or `12 / ∞`. */
export function capLabel(promo: PromotionView, unlimited: string, locale: string): string {
  const redeemed = formatCount(promo.redemptionCount, locale);
  const cap =
    promo.maxRedemptions == null ? unlimited : formatCount(promo.maxRedemptions, locale);
  return `${redeemed} / ${cap}`;
}

export function validityLabel(promo: PromotionView, locale: string): string | null {
  if (!promo.validFrom && !promo.validUntil) return null;
  const from = civilOrRaw(promo.validFrom, locale);
  const until = civilOrRaw(promo.validUntil, locale);
  return tr('promotions.validity', { from: from ?? '—', until: until ?? '—' });
}

function civilOrRaw(value: string | undefined, locale: string): string | null {
  if (!value) return null;
  const day = value.slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return value;
  return formatCivilDate(day, locale);
}
